import { createFileRoute, Link, Outlet, redirect, useNavigate, useRouterState } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { saMenuGroups } from "@/lib/sa-menu";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, LogOut, ShieldCheck } from "lucide-react";

export const Route = createFileRoute("/_authenticated/sa")({
  beforeLoad: async () => {
    const { data } = await supabase.auth.getUser();
    const uid = data.user?.id;
    if (!uid) throw redirect({ to: "/auth" });
    const { data: prof } = await supabase.from("profiles").select("role").eq("id", uid).maybeSingle();
    if (prof?.role !== "system_admin") throw redirect({ to: "/dashboard" });
    return { systemAdmin: true as const };
  },
  component: SaLayout,
});

function SaLayout() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const signOut = async () => {
    await supabase.auth.signOut();
    navigate({ to: "/auth", replace: true });
  };

  return (
    <div className="min-h-screen flex w-full bg-muted/30">
      <aside className="hidden md:flex w-64 shrink-0 flex-col border-r bg-card">
        <div className="h-14 flex items-center gap-2 border-b px-4">
          <ShieldCheck className="h-5 w-5 text-primary" />
          <span className="font-semibold">Administração</span>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-4">
          <Link
            to="/sa"
            className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm ${pathname === "/sa" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
          >
            <LayoutDashboard className="h-4 w-4" /> Menu Principal
          </Link>
          {saMenuGroups.map((group) => (
            <div key={group.label}>
              <p className="px-3 pb-1 text-xs font-medium uppercase text-muted-foreground">{group.label}</p>
              <div className="space-y-1">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const url = `/sa/${item.slug}`;
                  return (
                    <Link
                      key={item.slug}
                      to="/sa/$slug"
                      params={{ slug: item.slug }}
                      className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm ${pathname === url ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                    >
                      <Icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 flex items-center border-b bg-card px-4 gap-2">
          <span className="text-sm font-medium text-muted-foreground">
            Administrador Geral do Sistema
          </span>
          <div className="ml-auto">
            <Button variant="ghost" size="sm" onClick={signOut}>
              <LogOut className="h-4 w-4 mr-2" /> Sair
            </Button>
          </div>
        </header>
        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
